'use client'

import {
  buildSeoReportPath,
  getSeoReportCategory,
  getSeoReportSubcategory,
  type PublishedResearchReport,
  resolveReportSubcategorySlug,
} from '@dupe/common/research-report'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AnalyticsEventName } from '@dupe/common/constants'
import Layout from '~/src/components/Layout'
import useAnalytics from '~/src/hooks/useAnalytics'
import { Skeleton } from '~/src/components/ui/skeleton'
import { useSeoReportById } from '~/src/hooks/admin/useSeoReports'
import useAuthUser from '~/src/hooks/useAuthUser'
import { createPublicUrl } from '~/src/lib/helpers'
import { deriveSeoAdminReportState } from '~/src/lib/admin/seoReportState'
import {
  collectReportSources,
  type ReportSourceLink,
} from '~/src/lib/reports/reportSources'
import { serializeJsonLd } from '~/src/lib/reports/jsonLd'
import {
  buildFaqPageJsonLd,
  extractSchemaFaqItems,
} from '~/src/lib/reports/schemaJsonLd'
import type { PublishedResearchReportPageReport } from '~/src/lib/reports/publishedResearchReportPage'
import { useScrollEngagementTracking } from '~/src/lib/reports/useScrollEngagementTracking'
import { useSeoReportRetailerLinks } from '~/src/hooks/useSeoReportRetailerLinks'
import { SeoReportHeader } from './SeoReportHeader'
import { SeoReportHeroImage } from './SeoReportHeroImage'
import { SeoReportAuthor } from './SeoReportAuthor'
import { SeoReportDisclosure } from './SeoReportDisclosure'
import { SeoReportQuickAnswer } from './SeoReportQuickAnswer'
import { SeoReportTableOfContents } from './SeoReportTableOfContents'
import { SeoReportTopPicks } from './SeoReportTopPicks'
import {
  SeoReportHowWePicked,
  SeoReportWhoItsFor,
} from './SeoReportHowWePicked'
import { SeoReportRankedProduct } from './SeoReportRankedProduct'
import {
  buildCommunityQuotes,
  buildGeneratedQuoteState,
  buildGeneratedSocialSignalsState,
} from './reportProductEvidencePresentation'
import { SeoReportNotableMentions } from './SeoReportNotableMentions'
import { SeoReportComparisonTable } from './SeoReportComparisonTable'
import { SeoReportVisualSignals } from './SeoReportVisualSignals'
import { SeoReportWhatToKnow } from './SeoReportWhatToKnow'
import { SeoReportSkipThisOne } from './SeoReportSkipThisOne'
import { SeoReportRelatedReports } from './SeoReportRelatedReports'
import {
  hasSeoReportSources,
  SeoReportSources,
  SeoReportSourcesDrawer,
} from './SeoReportSources'
import { SeoReportProductDrawer } from './SeoReportProductDrawer'
import {
  SeoReportAnalyticsProvider,
  type SeoReportAnalyticsCallbacks,
} from './SeoReportAnalyticsContext'
import {
  classifyEntryReferrer,
  readEntryRefParam,
} from '~/src/lib/classifyEntryReferrer'

const SeoReportAdminControls = dynamic(
  () =>
    import('~/src/components/reports/SeoReportAdminControls').then(
      (m) => m.SeoReportAdminControls
    ),
  { ssr: false }
)

interface SeoResearchReportPageProps {
  report: PublishedResearchReportPageReport | PublishedResearchReport
}

type SeoResearchRenderableReport =
  | PublishedResearchReportPageReport
  | PublishedResearchReport

function getReportSourceSummary(report: SeoResearchRenderableReport): {
  sourceLinks: ReportSourceLink[]
  productCount: number
} {
  if ('sourceLinks' in report) {
    return {
      sourceLinks: report.sourceLinks,
      productCount: report.sourceProductCount,
    }
  }

  const products = report.document?.products ?? report.seo.rankedProducts
  return {
    sourceLinks: collectReportSources(products),
    productCount: products.length,
  }
}

function SectionSkeleton({
  eyebrow,
  title,
  lines = 3,
}: {
  eyebrow: string
  title: string
  lines?: number
}) {
  return (
    <section className='rounded-2xl border bg-white p-6 shadow-sm'>
      <p className='mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500'>
        {eyebrow}
      </p>
      <h2 className='text-2xl font-semibold'>{title}</h2>
      <div className='mt-4 space-y-3'>
        {Array.from({ length: lines }, (_, index) => (
          <Skeleton
            key={`${eyebrow}-${index}`}
            className={index === lines - 1 ? 'h-4 w-2/3' : 'h-4 w-full'}
          />
        ))}
      </div>
    </section>
  )
}

export function SeoResearchReportPage({ report }: SeoResearchReportPageProps) {
  const { isAdmin } = useAuthUser()
  const { data: liveAdminReport } = useSeoReportById(
    isAdmin ? report.id : undefined,
    { live: true }
  )
  const adminReport =
    isAdmin && liveAdminReport?.id === report.id && liveAdminReport.seo
      ? liveAdminReport
      : null
  const currentReport: SeoResearchRenderableReport = adminReport ?? report
  const currentSeo = currentReport.seo
  // The report's own subcategory drives the fragrance quote-card rule. The
  // public payload already carries it resolved; the live admin report is the
  // raw row, so resolve it the same way (never `seo.meta.subcategorySlug`,
  // which an admin category override nulls).
  const reportSubcategorySlug = adminReport
    ? resolveReportSubcategorySlug(adminReport)
    : (report.subcategorySlug ?? null)
  // The admin API doesn't fetch related links, so preserve the SSR-provided ones
  const relatedLinks =
    currentSeo.relatedLinks.length > 0
      ? currentSeo.relatedLinks
      : report.seo.relatedLinks
  const adminState = adminReport ? deriveSeoAdminReportState(adminReport) : null
  const canonicalPath = buildSeoReportPath({
    slug: currentReport.slug,
    categorySlug: currentSeo.meta.categorySlug,
    subcategorySlug: currentSeo.meta.subcategorySlug,
  })
  const showImageCheckSkeleton = Boolean(
    adminState?.componentStates.image_check.isProcessing
  )
  const showHeroSkeleton = Boolean(
    showImageCheckSkeleton ||
      adminState?.componentStates.hero_image.isProcessing
  )
  const showQuickAnswerSkeleton = Boolean(
    adminState?.componentStates.quick_answer.isProcessing
  )
  const showWhoItsForSkeleton = Boolean(
    adminState?.componentStates.who_its_for.isProcessing
  )
  const showTopPickDescriptorsSkeleton = Boolean(
    adminState?.componentStates.top_pick_descriptors.isProcessing
  )
  const showVisualSignalsSkeleton = Boolean(
    adminState?.componentStates.visual_signals.isProcessing
  )
  const showHowWePickedSkeleton = Boolean(
    adminState?.componentStates.how_we_picked.isProcessing
  )
  const showFaqSkeleton = Boolean(adminState?.componentStates.faq.isProcessing)
  const shouldRenderHero = Boolean(currentSeo.heroImage) || showHeroSkeleton
  const canonicalUrl = createPublicUrl(canonicalPath)

  const breadcrumbs: Array<{ label: string; href: string | null }> = []
  if (currentSeo.meta.categorySlug) {
    const categoryLabel =
      getSeoReportCategory(currentSeo.meta.categorySlug)?.label ??
      currentSeo.meta.categorySlug
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    breadcrumbs.push({
      label: categoryLabel,
      href: `/reports/category/${currentSeo.meta.categorySlug}`,
    })
    if (currentSeo.meta.subcategorySlug) {
      const subcategoryLabel =
        getSeoReportSubcategory(
          currentSeo.meta.categorySlug,
          currentSeo.meta.subcategorySlug
        )?.label ??
        currentSeo.meta.subcategorySlug
          .replace(/-/g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      breadcrumbs.push({
        label: subcategoryLabel,
        href: `/reports/category/${currentSeo.meta.categorySlug}/${currentSeo.meta.subcategorySlug}`,
      })
    }
  }
  // Final crumb is the current page (no link).
  breadcrumbs.push({ label: currentSeo.meta.h1, href: null })

  const topPickProducts = currentSeo.rankedProducts.slice(0, 4)
  // Product deep-dive drawer state — declared before the retailer-link
  // fetch so we can lazy-add the drawer's slug to the lookup list when
  // it opens (instead of pre-fetching links for every ranked product).
  const analytics = useAnalytics()
  const viewedRef = useRef(false)

  const [drawerSlug, setDrawerSlug] = useState<string | null>(null)
  const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState(false)
  // Initial-paint product surfaces render snapshot links from the static
  // report payload. Fetch live retailer links only after drawer interaction
  // to keep crawlers and first load off the JSON endpoint — except products
  // whose snapshot has zero links, which the hook fetches immediately so
  // their cards aren't stuck empty until someone opens the drawer.
  const retailerLinkSlugs = Array.from(
    new Set([
      ...currentSeo.topPicks.map((pick) => pick.slug),
      ...topPickProducts.map((product) => product.slug),
      ...(drawerSlug ? [drawerSlug] : []),
    ])
  )
  const retailerLinksBySlug = useSeoReportRetailerLinks(
    currentReport,
    retailerLinkSlugs,
    { enabled: drawerSlug != null }
  )
  const generatedQuoteState = useMemo(
    () => buildGeneratedQuoteState(currentReport.generatedComponents),
    [currentReport.generatedComponents]
  )
  const generatedSocialSignalsState = useMemo(
    () => buildGeneratedSocialSignalsState(currentReport.generatedComponents),
    [currentReport.generatedComponents]
  )

  // Mapping of every ranked product → its first purchase URL, used to
  // auto-link product-name mentions inside body copy (summary paragraphs).
  const productLinks = useMemo(() => {
    return currentSeo.rankedProducts
      .map((product) => {
        const url =
          product.retailerLinks?.buyLinks?.[0]?.url ??
          product.retailerLinks?.priceSourceLinks?.[0]?.url ??
          null
        if (!url) return null
        return { name: product.name, url }
      })
      .filter((entry): entry is { name: string; url: string } => entry !== null)
  }, [currentSeo.rankedProducts])

  const drawerProduct = useMemo(
    () =>
      drawerSlug
        ? (currentSeo.rankedProducts.find((p) => p.slug === drawerSlug) ?? null)
        : null,
    [drawerSlug, currentSeo.rankedProducts]
  )
  const drawerProductPosition = useMemo(() => {
    if (!drawerSlug) return 0
    const index = currentSeo.rankedProducts.findIndex(
      (product) => product.slug === drawerSlug
    )
    return index >= 0 ? index : 0
  }, [drawerSlug, currentSeo.rankedProducts])
  const sourceSummary = useMemo(
    () => getReportSourceSummary(currentReport),
    [currentReport]
  )
  const hasSourcesDrawer = hasSeoReportSources(sourceSummary.sourceLinks)
  const tocEntries = useMemo(
    () =>
      currentSeo.sections.map((s) => ({
        id: s.id,
        label: s.title,
        depth: s.type === 'ranked-product' ? 1 : 0,
      })),
    [currentSeo.sections]
  )

  // --- Analytics: page viewed ---
  useEffect(() => {
    if (viewedRef.current) return
    viewedRef.current = true
    // Effects only run post-hydration, so window is available and there is no
    // pages-router `router.isReady` gate to wait for.
    analytics.track(
      AnalyticsEventName.SeoReportViewed,
      {
        report_id: currentReport.id,
        report_slug: currentReport.slug,
        category_slug: currentSeo.meta.categorySlug ?? null,
        subcategory_slug: currentSeo.meta.subcategorySlug ?? null,
        product_count: currentSeo.rankedProducts.length,
        has_quick_answer: Boolean(currentSeo.quickAnswer),
        has_comparison_table: Boolean(currentSeo.comparisonTable),
        entry_referrer: classifyEntryReferrer(readEntryRefParam()),
      },
      { sendToPlausible: true }
    )
  }, [
    analytics,
    currentReport.id,
    currentReport.slug,
    currentSeo.meta.categorySlug,
    currentSeo.meta.subcategorySlug,
    currentSeo.rankedProducts.length,
    currentSeo.quickAnswer,
    currentSeo.comparisonTable,
  ])

  const handlePageEngaged = useCallback(() => {
    analytics.track(
      AnalyticsEventName.SeoReportEngaged,
      {
        report_id: currentReport.id,
        report_slug: currentReport.slug,
        category_slug: currentSeo.meta.categorySlug ?? null,
      },
      { sendToPlausible: true }
    )
  }, [
    analytics,
    currentReport.id,
    currentReport.slug,
    currentSeo.meta.categorySlug,
  ])

  useScrollEngagementTracking({ onEngaged: handlePageEngaged })

  // --- Analytics: drawer / sources open ---
  const handleDrawerOpen = useCallback(
    (slug: string) => {
      setDrawerSlug(slug)
      const product = currentSeo.rankedProducts.find((p) => p.slug === slug)
      analytics.track(AnalyticsEventName.SeoReportProductDrawerOpened, {
        report_id: currentReport.id,
        report_slug: currentReport.slug,
        product_slug: slug,
        product_name: product?.name ?? slug,
      })
    },
    [analytics, currentReport.id, currentReport.slug, currentSeo.rankedProducts]
  )

  const handleSourcesOpen = useCallback(() => {
    setSourcesDrawerOpen(true)
    analytics.track(AnalyticsEventName.SeoReportSourcesOpened, {
      report_id: currentReport.id,
      report_slug: currentReport.slug,
    })
  }, [analytics, currentReport.id, currentReport.slug])

  // --- Analytics: product + related click callbacks (context) ---
  const handleProductClick = useCallback<
    SeoReportAnalyticsCallbacks['onProductClick']
  >(
    (params) => {
      analytics.track(
        AnalyticsEventName.SeoReportProductClicked,
        {
          report_id: currentReport.id,
          report_slug: currentReport.slug,
          product_slug: params.productSlug,
          product_name: params.productName,
          outbound_url: params.outboundUrl,
          price: params.price,
          position: params.position,
          section: params.section,
        },
        { sendToPlausible: true, sendToTinybird: true }
      )
    },
    [analytics, currentReport.id, currentReport.slug]
  )

  const handleRelatedClick = useCallback<
    SeoReportAnalyticsCallbacks['onRelatedClick']
  >(
    (params) => {
      analytics.track(
        AnalyticsEventName.SeoReportRelatedClicked,
        {
          report_id: currentReport.id,
          report_slug: currentReport.slug,
          target_slug: params.targetSlug,
          target_title: params.targetTitle,
          position: params.position,
        },
        { sendToPlausible: true }
      )
    },
    [analytics, currentReport.id, currentReport.slug]
  )

  const seoReportAnalytics = useMemo<SeoReportAnalyticsCallbacks>(
    () => ({
      onProductClick: handleProductClick,
      onRelatedClick: handleRelatedClick,
    }),
    [handleProductClick, handleRelatedClick]
  )
  const faqPageJsonLd = useMemo(
    () => buildFaqPageJsonLd(extractSchemaFaqItems(currentSeo.jsonLd.faqPage)),
    [currentSeo.jsonLd.faqPage]
  )

  return (
    <>
      {/* --- Consolidated JSON-LD: all schemas from projection ---
          Rendered as inline body scripts (valid for Google structured data)
          instead of next/head so the component works under both the App
          Router (/reports) and the pages-router admin previews. <head> tags
          (title/canonical/OG/robots) are owned by generateMetadata in
          src/app/reports/[...reportPath]/page.tsx. */}
      {currentSeo.jsonLd.itemList && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(currentSeo.jsonLd.itemList),
          }}
        />
      )}
      {currentSeo.jsonLd.products?.map((productJsonLd, index) => (
        <script
          key={`product-json-ld-${index}`}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(productJsonLd),
          }}
        />
      ))}
      {currentSeo.jsonLd.breadcrumbList && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(currentSeo.jsonLd.breadcrumbList),
          }}
        />
      )}
      {currentSeo.jsonLd.article && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(currentSeo.jsonLd.article),
          }}
        />
      )}
      {faqPageJsonLd && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: serializeJsonLd(faqPageJsonLd),
          }}
        />
      )}

      <Layout hideBorders fullWidth>
        <SeoReportAnalyticsProvider value={seoReportAnalytics}>
          {/* Affiliate disclosure pinned above the header, mirroring the hub
              pages (FTC + EEAT requirement). */}
          <div className='mx-auto w-full max-w-6xl px-4 pt-6 md:px-10'>
            <SeoReportDisclosure />
          </div>
          {/* --- Full-width top section (above sidebar layout) --- */}
          <div className='mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 pt-8 md:gap-10 md:px-10'>
            <SeoReportHeader
              breadcrumbs={breadcrumbs}
              h1={currentSeo.meta.h1}
              freshnessStatement={currentSeo.meta.freshnessStatement ?? ''}
              shareUrl={canonicalUrl}
              productsAnalyzed={currentSeo.evidence?.productsAnalyzed ?? null}
              sourcesReviewed={currentSeo.evidence?.sourcesReviewed ?? null}
              onSourcesClick={hasSourcesDrawer ? handleSourcesOpen : undefined}
            />

            {/* Admin publication state is surfaced in the floating admin
                controls (SeoReportAdminControls), so no inline banner here. */}

            {shouldRenderHero && (
              <SeoReportHeroImage
                url={
                  showHeroSkeleton ? null : (currentSeo.heroImage?.url ?? null)
                }
                alt={
                  currentSeo.heroImage?.alt ??
                  `${currentSeo.meta.h1} hero image`
                }
                isGenerating={showHeroSkeleton}
              />
            )}
          </div>

          {/* Mobile sticky TOC */}
          <div className='sticky top-0 z-40 lg:hidden'>
            <SeoReportTableOfContents variant='mobile' entries={tocEntries} />
          </div>

          {/* --- Sidebar + Main content layout --- */}
          <div className='mx-auto flex w-full max-w-6xl gap-12 px-4 pb-20 pt-10 md:px-10'>
            {/* TOC sidebar: sticky on desktop, hidden on mobile (TOC component handles its own mobile toggle) */}
            <aside className='relative z-30 hidden w-[220px] shrink-0 lg:block'>
              <div className='sticky top-6'>
                <SeoReportTableOfContents
                  variant='desktop'
                  entries={tocEntries}
                />
              </div>
            </aside>

            {/* Main content column */}
            {/* color-token-exception: section dividers darkened per CEO feedback; border-border is too light at 8% opacity */}
            <article className='flex min-w-0 flex-1 flex-col gap-12 [&>*+*]:border-t [&>*+*]:border-black/30 [&>*+*]:pt-12 [&>[id]]:scroll-mt-14 [&>div>[id]]:scroll-mt-14'>
              {/* Author byline + quick answer grouped to avoid divider between them */}
              <div className='flex flex-col gap-6'>
                <SeoReportAuthor categorySlug={currentSeo.meta.categorySlug} />

                {showQuickAnswerSkeleton ? (
                  <SectionSkeleton
                    eyebrow='Quick answer'
                    title='Generating updated answer'
                  />
                ) : currentSeo.quickAnswer ? (
                  <SeoReportQuickAnswer
                    title={currentSeo.quickAnswer.title}
                    body={currentSeo.quickAnswer.body}
                    productLinks={productLinks}
                  />
                ) : null}
              </div>

              <section id='top-picks' className='scroll-mt-14'>
                {showTopPickDescriptorsSkeleton && (
                  <div className='mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700'>
                    Regenerating pick descriptors...
                  </div>
                )}
                <SeoReportTopPicks
                  isImageChecking={showImageCheckSkeleton}
                  picks={currentSeo.topPicks.map((pick) => ({
                    slug: pick.slug,
                    roleLabel: pick.roleLabel,
                    roleDescription: pick.roleDescription ?? pick.roleLabel,
                    name: pick.name,
                    brand: pick.brand ?? null,
                    image: pick.image ?? null,
                    summary: pick.summary ?? null,
                    buyLinks: retailerLinksBySlug.get(pick.slug) ?? [],
                  }))}
                  onViewProduct={handleDrawerOpen}
                />
              </section>

              {showWhoItsForSkeleton ? (
                <SectionSkeleton
                  eyebrow='Audience'
                  title='Generating shopper fit'
                  lines={3}
                />
              ) : currentSeo.methodology?.whoItsFor ? (
                <section id='who-this-is-for' className='scroll-mt-14'>
                  <SeoReportWhoItsFor
                    body={currentSeo.methodology.whoItsFor.body}
                  />
                </section>
              ) : null}

              {showHowWePickedSkeleton ? (
                <SectionSkeleton
                  eyebrow='Evaluation'
                  title='Generating evaluation criteria'
                  lines={4}
                />
              ) : currentSeo.methodology?.guide && currentSeo.evidence ? (
                <section id='how-we-picked' className='scroll-mt-14'>
                  <SeoReportHowWePicked
                    title={currentSeo.methodology.title}
                    subtitle={
                      currentSeo.methodology.intro ??
                      'This is the evaluation criteria and key considerations our agents used when choosing products for this report.'
                    }
                    criteria={currentSeo.methodology.guide.insights.map(
                      (insight) => ({
                        label: insight.heading,
                        description: insight.body,
                        icon: insight.icon ?? undefined,
                      })
                    )}
                  />
                </section>
              ) : null}

              {topPickProducts.map((product, index) => (
                <SeoReportRankedProduct
                  key={product.slug}
                  slug={product.slug}
                  isTopPick={index === 0}
                  rank={product.ranking?.rank ?? index + 1}
                  name={product.name}
                  brand={product.brand}
                  roleLabel={
                    currentSeo.topPicks.find((p) => p.slug === product.slug)
                      ?.roleLabel ?? null
                  }
                  roleDescription={
                    currentSeo.topPicks.find((p) => p.slug === product.slug)
                      ?.roleDescription ?? null
                  }
                  images={product.images}
                  summary={product.assessment?.summary ?? null}
                  pricing={product.pricing}
                  buyLinks={retailerLinksBySlug.get(product.slug) ?? []}
                  specs={Object.entries(product.specs ?? {})
                    .filter(([key, val]) => {
                      if (key.match(/rating|avg_user_review|review_count/i))
                        return false
                      // Drop empty values plus the footwear-taxonomy classifier
                      // keys (Clogs/Flats/Mules/etc.) that are noise on
                      // non-matching products. Value-based "No" filtering
                      // would incorrectly hide legitimate specs like
                      // "Noise cancelling: No" on other product types.
                      if (val == null) return false
                      if (typeof val === 'string' && val.trim() === '')
                        return false
                      if (typeof val === 'boolean' && !val) return false
                      const normalizedKey = key
                        .replace(/[\s_-]+/g, '')
                        .toLowerCase()
                      if (
                        [
                          'clogs',
                          'flats',
                          'heels',
                          'mules',
                          'wedges',
                          'sandals',
                          'boots',
                          'loafers',
                          'oxfords',
                          'pumps',
                          'slippers',
                        ].includes(normalizedKey)
                      ) {
                        return false
                      }
                      return true
                    })
                    .slice(0, 8)
                    .map(([key, val]) => ({
                      label: key
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase()),
                      value: val != null ? String(val) : '-',
                    }))}
                  rating={(() => {
                    const attrs = { ...product.specs, ...product.attributes }
                    const score = Number(
                      attrs?.rating ??
                        attrs?.avg_user_reviews ??
                        attrs?.average_rating
                    )
                    if (!score || isNaN(score)) return null
                    const count = String(
                      attrs?.review_count ?? attrs?.rating_count ?? ''
                    )
                    return { score, count: count || null }
                  })()}
                  highlights={product.assessment?.pros ?? []}
                  worthKnowing={product.assessment?.cons ?? []}
                  communityQuotes={buildCommunityQuotes(
                    product,
                    generatedSocialSignalsState,
                    generatedQuoteState,
                    reportSubcategorySlug
                  )}
                  onViewProduct={handleDrawerOpen}
                  isImageChecking={showImageCheckSkeleton}
                  productLinks={productLinks}
                  analyticsPosition={index}
                />
              ))}

              {currentSeo.notableMentions &&
                currentSeo.notableMentions.length > 0 && (
                  <section id='notable-mentions' className='scroll-mt-14'>
                    <SeoReportNotableMentions
                      mentions={currentSeo.notableMentions}
                      onViewProduct={handleDrawerOpen}
                    />
                  </section>
                )}

              {currentSeo.comparisonTable && (
                <section id='key-spec-comparison' className='scroll-mt-14'>
                  <SeoReportComparisonTable
                    title='Key spec comparison'
                    columns={currentSeo.comparisonTable.columns}
                    rows={currentSeo.comparisonTable.rows}
                  />
                </section>
              )}

              {showVisualSignalsSkeleton ? (
                <SectionSkeleton
                  eyebrow='Visual signals'
                  title='Generating visual comparison'
                  lines={4}
                />
              ) : currentSeo.visualSignals ? (
                <SeoReportVisualSignals
                  visualSignals={currentSeo.visualSignals}
                />
              ) : null}

              {showFaqSkeleton ? (
                <SectionSkeleton
                  eyebrow='FAQ'
                  title='Generating FAQ'
                  lines={5}
                />
              ) : currentSeo.faq.length > 0 ? (
                <section id='what-to-know' className='scroll-mt-14'>
                  <SeoReportWhatToKnow
                    title='What to know before buying'
                    items={currentSeo.faq.map((entry) => ({
                      question: entry.question,
                      answer: entry.answer,
                    }))}
                  />
                </section>
              ) : null}

              {currentSeo.skipProduct && (
                <section id='skip-this-one' className='scroll-mt-14'>
                  <SeoReportSkipThisOne
                    name={currentSeo.skipProduct.name}
                    image={currentSeo.skipProduct.image}
                    summary={currentSeo.skipProduct.summary}
                    reasons={currentSeo.skipProduct.reasons}
                  />
                </section>
              )}

              {relatedLinks.length > 0 && (
                <section id='related-reports' className='scroll-mt-14'>
                  <SeoReportRelatedReports
                    reports={relatedLinks.map((link) => ({
                      slug: link.slug,
                      title: link.title,
                      imageUrl: link.imageUrl ?? null,
                    }))}
                  />
                </section>
              )}

              {/* Server-rendered citation list. The "X sources" drawer in the
                header stays as the engaged-user surface; this footer block is
                what Google indexes — every external research link in the
                SSR HTML with a real href. */}
              {hasSourcesDrawer && (
                <section id='sources' className='scroll-mt-14'>
                  <SeoReportSources
                    sources={sourceSummary.sourceLinks}
                    productCount={sourceSummary.productCount}
                    sourcesReviewed={
                      currentSeo.evidence?.sourcesReviewed ?? null
                    }
                    compact
                  />
                </section>
              )}
            </article>
          </div>
        </SeoReportAnalyticsProvider>
      </Layout>

      {adminReport && <SeoReportAdminControls report={adminReport} />}

      <SeoReportProductDrawer
        open={drawerSlug != null}
        onOpenChange={(open) => {
          if (!open) setDrawerSlug(null)
        }}
        product={drawerProduct}
        buyLinks={drawerSlug ? (retailerLinksBySlug.get(drawerSlug) ?? []) : []}
        onBuyLinkClick={handleProductClick}
        analyticsPosition={drawerProductPosition}
        communityQuotes={
          drawerProduct
            ? buildCommunityQuotes(
                drawerProduct,
                generatedSocialSignalsState,
                generatedQuoteState,
                reportSubcategorySlug
              )
            : []
        }
      />

      <SeoReportSourcesDrawer
        open={sourcesDrawerOpen}
        onOpenChange={setSourcesDrawerOpen}
        sources={sourceSummary.sourceLinks}
        productCount={sourceSummary.productCount}
        sourcesReviewed={currentSeo.evidence?.sourcesReviewed ?? null}
      />
    </>
  )
}
